import React from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, ChevronDown, Package, Database, Lock, Globe } from 'lucide-react';
import { Badge } from '@workspace/ui';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface ApiParameter {
  name: string;
  in: 'path' | 'query' | 'header';
  required: boolean;
  description?: string;
  defaultValue?: string;
  type?: string;
  enum?: string[];
  format?: string;
}

export interface ApiBodyField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  example?: string;
  defaultValue?: string;
  enum?: string[];
  format?: string;
}

export interface ApiRequestBody {
  description?: string;
  required?: boolean;
  fields: ApiBodyField[];
}

export interface ApiEndpoint {
  path: string;
  method: HttpMethod;
  summary: string;
  tag: string;
  parameters?: ApiParameter[];
  requestBody?: ApiRequestBody;
}

interface EndpointExplorerProps {
  endpoints: ApiEndpoint[];
  selectedPath?: string;
  selectedMethod?: HttpMethod;
  onSelect: (endpoint: ApiEndpoint) => void;
  searchQuery?: string;
}

export function EndpointExplorer({
  endpoints,
  selectedPath,
  selectedMethod,
  onSelect,
  searchQuery = ''
}: EndpointExplorerProps) {
  // Group endpoints by tag
  const grouped = React.useMemo(() => {
    const filtered = endpoints.filter(e => 
      e.path.toLowerCase().includes(searchQuery.toLowerCase()) || 
      e.summary.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const groups: Record<string, ApiEndpoint[]> = {};
    filtered.forEach(e => {
      if (!groups[e.tag]) groups[e.tag] = [];
      groups[e.tag].push(e);
    });
    return groups;
  }, [endpoints, searchQuery]);

  return (
    <div className="flex flex-col h-full py-2">
      {Object.entries(grouped).map(([tag, items]) => (
        <EndpointGroup 
          key={tag} 
          tag={tag} 
          items={items} 
          selectedPath={selectedPath}
          selectedMethod={selectedMethod}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function EndpointGroup({ 
  tag, 
  items, 
  onSelect, 
  selectedPath, 
  selectedMethod 
}: { 
  tag: string; 
  items: ApiEndpoint[]; 
  onSelect: (endpoint: ApiEndpoint) => void;
  selectedPath?: string;
  selectedMethod?: HttpMethod;
}) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="flex flex-col">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-4 py-2 hover:bg-muted/50 transition-colors group"
      >
        {expanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground group-hover:text-foreground transition-colors">
          {tag}
        </span>
        <span className="ml-auto text-[10px] bg-muted px-1.5 rounded-full text-muted-foreground">
          {items.length}
        </span>
      </button>

      {expanded && (
        <div className="flex flex-col mb-2">
          {items.map(endpoint => {
            const isSelected = selectedPath === endpoint.path && selectedMethod === endpoint.method;
            
            return (
              <button
                key={`${endpoint.method}-${endpoint.path}`}
                onClick={() => onSelect(endpoint)}
                className={cn(
                  "flex flex-col gap-0.5 px-6 py-2 border-l-2 transition-all text-left",
                  isSelected 
                    ? "bg-primary/5 border-primary" 
                    : "border-transparent hover:bg-muted/30 hover:border-muted-foreground/20"
                )}
              >
                <div className="flex items-center gap-2">
                  <MethodBadge method={endpoint.method} />
                  <span className={cn(
                    "text-xs font-mono truncate",
                    isSelected ? "text-foreground font-bold" : "text-muted-foreground"
                  )}>
                    {endpoint.path}
                  </span>
                </div>
                <span className="text-[10px] text-muted-foreground/60 truncate pl-1">
                  {endpoint.summary}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function MethodBadge({ method }: { method: HttpMethod }) {
  const colors = {
    GET: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    POST: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    PUT: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    DELETE: "bg-rose-500/10 text-rose-600 border-rose-500/20",
    PATCH: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  };

  return (
    <span className={cn(
      "text-[9px] font-bold px-1 rounded border leading-tight min-w-[34px] text-center",
      colors[method] || "bg-muted text-muted-foreground"
    )}>
      {method}
    </span>
  );
}
